import { Component, OnInit } from '@angular/core';
import {
  AgGridComponentFrameworkComponent
} from "../../../../../shared/components/ag-grid/ag-grid-component-framework/ag-grid-component-framework.component";
import {CellSourceComponent} from "../../../../../shared/components/cell-source/cell-source.component";
import {Serializer, Serializers} from "../../../../../shared/interfaces/serializer";
import {CellSelectComponent} from "../../../../../shared/components/cell-select/cell-select.component";
import {TranslateService} from "@ngx-translate/core";
import {SelectOption} from "../../../../../shared/interfaces/form";
import {Visibility} from "../../../../../shared/interfaces/settings";
import {AppService} from "../../../../../shared/services/app.service";
import {SettingsService} from "../../../../../shared/services/settings.service";
import {Observable, withLatestFrom} from 'rxjs';
import {AppState} from "../../../../../shared/store/store";
import {filter, map} from 'rxjs/operators';
import {DataSetDimension, DataSetDimensionsGroup} from '../../../../../shared/interfaces/dimensions';
import {
  selectDataSetDimensionsLoading,
  selectDataSetDimensionSourceComponentObjects
} from '../../../../../shared/store/dataSetDimensions/dataSetDimensions.selectors';
import {Store} from '@ngrx/store';
import {
  selectDataSetDimensionsGroupComponentObjects,
  selectDataSetDimensionsGroups,
  selectDataSetDimensionsGroupsLoaded
} from '../../../../../shared/store/dataSetDimensionsGroups/dataSetDimensionsGroups.selectors';
import {updateDataSetDimension} from '../../../../../shared/store/dataSetDimensions/dataSetDimensions.actions';
import {DimensionComponentObject, DimensionComponentObjects} from '../../../../../shared/classes/dimension-component-object';
import {MetricComponentObject} from '../../../../../shared/classes/metric-component-object';
import {
  DatasetDimensionsGroupComponentObject,
  DatasetDimensionsGroupComponentObjects
} from '../../../../../shared/classes/dataset-dimensions-group-component-object';

@Component({
  selector: 'app-dimensions-source-table',
  templateUrl: './dimensions-source-table.component.html',
  styleUrls: ['./dimensions-source-table.component.scss']
})
export class DimensionsSourceTableComponent implements OnInit {

  public readonly defaultColDef: any = {
    resizable: true,
    suppressNavigable: true,
    suppressCellSelection: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset']
    }
  };

  public readonly columnDefs$: Observable<Array<any>> = this._store.select(selectDataSetDimensionsGroupsLoaded)
    .pipe(
      filter((loaded: boolean): boolean => loaded),
      withLatestFrom(this._store.select(selectDataSetDimensionsGroupComponentObjects)),
      map(([_, dataSetDimensionsGroups]: [boolean, DatasetDimensionsGroupComponentObjects]) => {
        return [
          {
            minWidth: 200,
            sortable: true,
            filter: 'agTextColumnFilter',
            lockPosition: true,
            headerValueGetter: _ => {
              return this._translateS.instant('settings.dimension');
            },
            valueGetter: params => params.data.payload.attributes.name,
            cellRendererFramework: AgGridComponentFrameworkComponent,
            cellRendererParams: _ => {
              return {
                component:  CellSourceComponent,
                componentParams: {
                  sourceGetter: (row: MetricComponentObject) => row.payload,
                  showCategory: false
                }
              };
            }
          },
          {
            minWidth: 200,
            sortable: true,
            filter: 'agTextColumnFilter',
            lockPosition: true,
            valueGetter: params => params.data.payload.relationships.data_source.data.attributes.name,
            filterValueGetter: params => params.data.payload.relationships.data_source.data.attributes.name,
            headerValueGetter: _ => {
              return this._translateS.instant('settings.source');
            },
            cellRendererFramework: AgGridComponentFrameworkComponent,
            cellRendererParams: _ => {
              return {
                component: CellSourceComponent,
                componentParams: {
                  sourceGetter: (row: MetricComponentObject) => ({
                    name: row.payload.relationships.data_source.data.attributes.name,
                    icon: row.payload.relationships.data_source.data.attributes.icon_small
                  })
                }
              };
            }
          },
          {
            minWidth: 200,
            sortable: true,
            filter: true,
            lockPosition: true,
            tooltipComponent: 'customTooltip',
            headerTooltip: this._translateS.instant('settings.visibility_tooltip'),
            headerValueGetter: _ => {
              return this._translateS.instant('settings.visibility');
            },
            valueGetter: params => params.data.payload.attributes.visibility,
            cellRendererFramework: AgGridComponentFrameworkComponent,
            cellRendererParams: _ => {
              return {
                component:  CellSelectComponent,
                componentParams: this._getVisibilityParams()
              };
            },
            filterValueGetter: params => this._translateS.instant(`settings.${params.data.payload.attributes.visibility}`)
          },
          {
            minWidth: 200,
            sortable: true,
            filter: 'agTextColumnFilter',
            lockPosition: true,
            headerValueGetter: _ => this._translateS.instant('settings.category'),
            filterValueGetter: params => params.data.payload.relationships.data_set_dimensions_group.data?.attributes.name || this._translateS.instant('settings.source_menu_only'),
            valueGetter: params => params.data.payload.relationships.data_set_dimensions_group.data?.attributes.name,
            cellRendererFramework: AgGridComponentFrameworkComponent,
            cellRendererParams: _ => {
              return {
                component:  CellSelectComponent,
                componentParams: this._getCategoryParams(dataSetDimensionsGroups)
              };
            }
          }
        ];
      })
    )

  public readonly dimensions$: Observable<DimensionComponentObjects> = this._store.select(selectDataSetDimensionSourceComponentObjects);
  public readonly loading$: Observable<boolean> = this._store.select(selectDataSetDimensionsLoading);

  constructor(
    private readonly _translateS: TranslateService,
    private readonly _appS: AppService,
    private readonly _settingsS: SettingsService,
    private readonly _store: Store<AppState>
  ) { }

  ngOnInit(): void {}

  private _getCategoryParams(dimensionsCategory: DatasetDimensionsGroupComponentObjects): any {
    return {
      options:            [{id: '0', attributes: {name: 'settings.source_menu_only'}}, ...dimensionsCategory.map((group: DatasetDimensionsGroupComponentObject): Serializer<DataSetDimensionsGroup> => group.payload as Serializer<DataSetDimensionsGroup>)],
      disabled:           this._appS.isNotDatasetAdmin,
      defaultValueGetter: (dimension: DimensionComponentObject) => dimension.payload.relationships.data_set_dimensions_group.data?.id || '0',
      textGetter:         (category: Serializer<DataSetDimensionsGroup>) => category.attributes.name,
      valueGetter:        (category: Serializer<DataSetDimensionsGroup>) => category.id,
      onValueChanges:     (row: any, value: any) => this._updateDimension(row, { data_set_dimensions_group_id: value })
    }
  }

  private _getVisibilityParams(): any {
    return {
      options:              this._settingsS.visibilities,
      disabled:             this._appS.isNotDatasetAdmin,
      defaultValueGetter:   (dimension: DimensionComponentObject) => dimension.payload.attributes.visibility,
      textGetter:           (visibility: SelectOption<Visibility, string>) => visibility.text,
      valueGetter:          (visibility: SelectOption<Visibility, string>) => visibility.key,
      onValueChanges:       (row: any, value: any) => this._updateDimension(row, { visibility: value })
    }
  }

  private _updateDimension(dimension: DimensionComponentObject, update: Partial<DataSetDimension>): void {
    this._store.dispatch(updateDataSetDimension({
      dimension: dimension.payload as Serializer<DataSetDimension>,
      update: update
    }));

  }

}
